import React, { useEffect, useState } from 'react';
import { View, Text, Image, TouchableOpacity, StyleSheet, ActivityIndicator, FlatList } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { getUserPublishedPosts } from '@cubemeet/core/services/userProfileService';
import { MdVisibility, MdFavorite, MdInsertComment, MdLocationOn } from 'react-icons/md';

interface UserPost {
  id: string;
  author_id: string;
  title: string;
  summary?: string;
  cover_image?: string;
  city?: string;
  tags?: string[];
  view_count: number;
  like_count: number;
  comment_count: number;
  created_at: string;
}

interface UserPostsProps {
  userId: string;
}

const UserPosts: React.FC<UserPostsProps> = ({ userId }) => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<UserPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    loadPosts(1);
  }, [userId]);

  const loadPosts = async (pageNum: number) => {
    try {
      setLoading(true);
      const pageSize = 10;
      const { data, count } = await getUserPublishedPosts(userId, {
        page: pageNum,
        pageSize,
        sortBy: 'created_at',
        sortOrder: 'desc',
      });

      setPosts(data);
      setTotalCount(count);
      setTotalPages(Math.ceil(count / pageSize));
      setPage(pageNum);
    } catch (error) {
      console.error('加载游记失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPageWindow = (total: number, current: number) => {
    const maxVisible = 5;
    if (total <= maxVisible) return Array.from({ length: total }).map((_, i) => i + 1);
    let start = current - Math.floor(maxVisible / 2);
    if (start < 1) start = 1;
    let end = start + maxVisible - 1;
    if (end > total) {
      end = total;
      start = Math.max(1, end - maxVisible + 1);
    }
    return Array.from({ length: end - start + 1 }).map((_, i) => start + i);
  };

  if (loading && posts.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="small" color="#ff6b35" />
        <Text style={styles.loadingText}>加载游记中...</Text>
      </View>
    );
  }

  if (posts.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <Text style={styles.emptyText}>还没有发布任何游记</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {posts.map((post) => (
        <TouchableOpacity
          key={post.id}
          style={styles.postCard}
          onPress={() => navigate(`/travel-post/${post.id}`)}
          activeOpacity={0.7}
        >
          {/* 封面图 */}
          {post.cover_image && (
            <Image source={{ uri: post.cover_image }} style={styles.coverImage} />
          )}

          {/* 内容 */}
          <View style={styles.postContent}>
            <Text style={styles.postTitle} numberOfLines={2}>
              {post.title}
            </Text>

            {post.summary && (
              <Text style={styles.postSummary} numberOfLines={2}>
                {post.summary}
              </Text>
            )}

            {/* 元数据 */}
            <View style={styles.postMeta}>
              <View style={styles.statsRow}>
                <View style={styles.statItem}>
                  <MdVisibility size={14} color="#999" />
                  <Text style={styles.statText}>{post.view_count}</Text>
                </View>
                <Text style={styles.statDivider}>·</Text>
                <View style={styles.statItem}>
                  <MdFavorite size={14} color="#999" />
                  <Text style={styles.statText}>{post.like_count}</Text>
                </View>
                <Text style={styles.statDivider}>·</Text>
                <View style={styles.statItem}>
                  <MdInsertComment size={14} color="#999" />
                  <Text style={styles.statText}>{post.comment_count}</Text>
                </View>
              </View>
              
              {post.city && (
                <View style={styles.locationRow}>
                  <MdLocationOn size={14} color="#0ea5a4" />
                  <Text style={styles.cityText}>{post.city}</Text>
                </View>
              )}
            </View>
          </View>
        </TouchableOpacity>
      ))}

      {/* 分页控件 */}
      {totalPages > 1 && (
        <View style={styles.paginationContainer}>
          <TouchableOpacity
            style={[styles.pageButton, page === 1 && styles.pageButtonDisabled]}
            onPress={() => { if (page > 1) loadPosts(1); }}
          >
            <Text style={styles.pageButtonText}>«</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.pageButton, page === 1 && styles.pageButtonDisabled]}
            onPress={() => { if (page > 1) loadPosts(page - 1); }}
          >
            <Text style={styles.pageButtonText}>‹</Text>
          </TouchableOpacity>

          {getPageWindow(totalPages, page).map((p) => (
            <TouchableOpacity
              key={p}
              style={[styles.pageButton, page === p && styles.pageButtonActive]}
              onPress={() => { if (p !== page) loadPosts(p); }}
            >
              <Text style={[styles.pageButtonText, page === p && styles.pageButtonTextActive]}>{p}</Text>
            </TouchableOpacity>
          ))}

          <TouchableOpacity
            style={[styles.pageButton, page === totalPages && styles.pageButtonDisabled]}
            onPress={() => { if (page < totalPages) loadPosts(page + 1); }}
          >
            <Text style={styles.pageButtonText}>›</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.pageButton, page === totalPages && styles.pageButtonDisabled]}
            onPress={() => { if (page < totalPages) loadPosts(totalPages); }}
          >
            <Text style={styles.pageButtonText}>»</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingVertical: 8,
  },

  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 20,
    gap: 8,
  },
  loadingText: {
    fontSize: 13,
    color: '#666',
  },

  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyText: {
    fontSize: 14,
    color: '#ccc',
  },

  postCard: {
    marginBottom: 12,
    backgroundColor: '#fff',
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#f0f0f0',
  },

  coverImage: {
    width: '100%',
    aspectRatio: 16 / 9,
    backgroundColor: '#f0f0f0',
  },

  postContent: {
    paddingHorizontal: 12,
    paddingVertical: 10,
  },

  postTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 6,
  },

  postSummary: {
    fontSize: 12,
    color: '#666',
    lineHeight: 18,
    marginBottom: 8,
  },

  postMeta: {
    gap: 6,
  },

  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },

  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },

  statText: {
    fontSize: 11,
    color: '#999',
  },

  statDivider: {
    fontSize: 11,
    color: '#ccc',
    marginHorizontal: 6,
  },

  locationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },

  cityText: {
    fontSize: 11,
    color: '#0ea5a4',
    fontWeight: '500',
  },

  loadMoreButton: {
    paddingVertical: 12,
    alignItems: 'center',
  },

  loadMoreText: {
    fontSize: 13,
    color: '#0ea5a4',
    fontWeight: '500',
  },

  paginationContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 12,
    flexWrap: 'nowrap',
    alignSelf: 'center',
    marginHorizontal: 12,
  },
  pageButton: { minWidth: 36, paddingHorizontal: 10, paddingVertical: 8, borderRadius: 8, marginHorizontal: 4, borderWidth: 1, borderColor: '#E5E7EB', backgroundColor: '#fff', alignItems: 'center', justifyContent: 'center' },
  pageButtonActive: { backgroundColor: '#ff6b35', borderColor: '#ff6b35' },
  pageButtonDisabled: { opacity: 0.5 },
  pageButtonText: { fontSize: 14, color: '#111827' },
  pageButtonTextActive: { color: '#fff', fontWeight: '700' },
});

export default UserPosts;
